import { Trans, useLingui } from "@lingui/react/macro";
import { useState } from "react";
import { Link } from "react-router-dom";

import type { AppModel } from "../../../app/app-model";

import { ErrorCode } from "../../../api";
import { apiErrorCode } from "../../../api/api-connector";
import { errorText } from "../../../app/error-text";
import { maybeReportError } from "../../../app/errors";
import { Button } from "../../../components/ui-kit/button";
import { ErrorMessage } from "../../../components/ui-kit/error-message";
import { PasswordInput } from "../../../components/ui-kit/fields/password-input";
import { TextInput } from "../../../components/ui-kit/fields/text-input";
import { Form } from "../../../components/ui-kit/form/form";
import { LoginNext, nextAfterLogin } from "./login-challenge";

export function PasswordLogin(props: {
  app: AppModel;
  onEmailLogin: () => void;
  onNext: (next: LoginNext) => void;
}) {
  const { t } = useLingui();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<unknown>();

  async function handleSubmit() {
    setError(undefined);
    try {
      const next = nextAfterLogin(
        await props.app.seerlinqApi.login(username, password),
      );

      if (next) {
        props.onNext(next);
      } else {
        props.app.seerlinqApi.completeLogin();
      }
    } catch (error) {
      maybeReportError(error);
      setError(error);
    }
  }

  /**
   * Wrong credentials are the common case but not the only one — telling a
   * locked-out user to check their password sends them into the same wall.
   */
  function loginErrorText(error: unknown) {
    switch (apiErrorCode(error)) {
      case ErrorCode.INVALID_CREDENTIALS:
        return t`Incorrect username or password`;

      case ErrorCode.RATE_LIMIT_EXCEEDED:
        return t`Too many login attempts. Please wait a few minutes and try again.`;

      case ErrorCode.USER_NOT_ACTIVE:
        return t`This account is not active. Please contact support.`;

      default:
        return errorText(error);
    }
  }

  return (
    <Form className="flex flex-col" onSubmit={handleSubmit}>
      <div className="grid grid-cols-[max-content_1fr] gap-x-2 gap-y-3 items-center">
        <label>
          <Trans>Username:</Trans>
        </label>
        <TextInput
          value={username}
          onChange={(newValue) => {
            setUsername(newValue);
          }}
          autoFocus
        />
        <label>
          <Trans>Password:</Trans>
        </label>
        <PasswordInput
          value={password}
          onChange={(newValue) => {
            setPassword(newValue);
          }}
        />
      </div>

      <ErrorMessage message={error ? loginErrorText(error) : ""} />

      <div className="flex items-center justify-between mt-6">
        <div className="flex flex-col gap-2">
          <Link
            to="/forgot-password"
            className="text-xs text-brand-primary hover:underline"
          >
            <Trans>Forgot password?</Trans>
          </Link>
          <a
            onClick={props.onEmailLogin}
            className="text-xs text-brand-primary hover:underline cursor-pointer"
          >
            <Trans>Log in with email link</Trans>
          </a>
        </div>

        <Button variant="primary" type="submit">
          <Trans>Log In</Trans>
        </Button>
      </div>
    </Form>
  );
}
